import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useReducer
} from 'react'
import { Modal, Spin, message, Tooltip } from 'antd'
import { usePresignedUpload } from '../../../../../hooks/usePresignedUpload'
import './DrawingCanvas.scss'

// ─── Constants ────────────────────────────────────────────────────────────────

const TOOLS = {
  PEN: 'pen',
  CIRCLE: 'circle',
  TEXT: 'text',
  ERASER: 'eraser',
  PAN: 'pan'
}

const DEFAULT_COLORS = [
  '#F44336',
  '#FF9800',
  '#FFEB3B',
  '#4CAF50',
  '#2196F3',
  '#9C27B0',
  '#E91E63',
  '#ffffff',
  '#1a1a1a'
]

const MAX_HISTORY = 30
const LOAD_DELAY_MS = 150
const MIN_ZOOM = 0.05
const MAX_ZOOM = 10
const ZOOM_STEP = 0.12

const blurActiveElement = () => {
  if (typeof document === 'undefined') return

  const activeElement = document.activeElement
  if (activeElement && typeof activeElement.blur === 'function') {
    activeElement.blur()
  }
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────

const Icons = {
  Pen: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.5 2.5l3 3L6 17H3v-3L14.5 2.5z" />
    </svg>
  ),
  Circle: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    >
      <circle cx="10" cy="10" r="7" />
    </svg>
  ),
  Text: () => (
    <svg viewBox="0 0 20 20" fill="currentColor">
      <path d="M3 4h14v2.5H11.5V16h-3V6.5H3V4z" />
    </svg>
  ),
  Eraser: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 16h14M6.5 16L3 12.5l8-8 4.5 4.5-4.5 4.5" />
      <path d="M15 4.5L12.5 7" />
    </svg>
  ),
  Pan: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 2v4M10 14v4M2 10h4M14 10h4" />
      <circle cx="10" cy="10" r="2.5" />
    </svg>
  ),
  Undo: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 9H13a4 4 0 010 8H9" />
      <path d="M4 9l3.5-3.5M4 9l3.5 3.5" />
    </svg>
  ),
  Clear: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="4 6 16 6" />
      <path d="M8 6V4h4v2M5 6l1 10h8l1-10" />
      <line x1="8" y1="10" x2="8" y2="14" />
      <line x1="12" y1="10" x2="12" y2="14" />
    </svg>
  ),
  ZoomIn: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    >
      <circle cx="9" cy="9" r="5.5" />
      <line x1="13.2" y1="13.2" x2="17" y2="17" />
      <line x1="9" y1="6.5" x2="9" y2="11.5" />
      <line x1="6.5" y1="9" x2="11.5" y2="9" />
    </svg>
  ),
  ZoomOut: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    >
      <circle cx="9" cy="9" r="5.5" />
      <line x1="13.2" y1="13.2" x2="17" y2="17" />
      <line x1="6.5" y1="9" x2="11.5" y2="9" />
    </svg>
  ),
  Fit: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8V3h5M12 3h5v5M17 12v5h-5M8 17H3v-5" />
    </svg>
  ),
  Check: () => (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 10 8 15 17 5" />
    </svg>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Load an image in a way that is ALWAYS safe to draw onto a canvas and export
 * via toBlob / toDataURL — i.e. it never taints the canvas.
 *
 * Strategy (in order):
 *  1. Try fetch() with credentials:'omit' — gets a CORS-clean response whose
 *     blob URL is same-origin, so the canvas stays untainted.
 *  2. If fetch fails (network error, opaque response, etc.), fall back to an
 *     <img crossOrigin="anonymous"> load — works when the server sends the
 *     correct Access-Control-Allow-Origin header.
 *  3. Last resort: load without CORS. The image WILL display but the canvas
 *     will be tainted. We still resolve so the user can at least draw; we
 *     attach a `tainted:true` flag so the save path can show a helpful error
 *     instead of a cryptic SecurityError.
 *
 * Returns Promise<HTMLImageElement>.  The element may have `.tainted = true`.
 */
/**
 * Load an image so the resulting canvas is never tainted and toBlob() works.
 *
 * The #1 cause of canvas taint is the browser cache: if the browser previously
 * fetched the URL *without* CORS headers (e.g. from an <img> elsewhere on the
 * page) it caches a non-CORS opaque response. A subsequent crossOrigin request
 * hits that cached response, gets rejected, and our old fallback loaded it
 * without CORS — tainting the canvas.
 *
 * Fix: append a cache-busting param so the browser always makes a fresh CORS
 * request, completely bypassing the poisoned cache entry.
 * The server (S3, CDN, etc.) ignores unknown query params so the URL still
 * resolves correctly.
 */
async function loadImage(src) {
  if (!src) throw new Error('No src')

  // Build a cache-busted URL so we never hit a stale non-CORS cache entry
  const cacheBustedSrc = (() => {
    try {
      const u = new URL(src, window.location.href)
      u.searchParams.set('_cb', Date.now())
      return u.toString()
    } catch {
      // Fallback for relative or unusual URLs
      const sep = src.includes('?') ? '&' : '?'
      return `${src}${sep}_cb=${Date.now()}`
    }
  })()

  // ── Strategy 1: fetch() → blob URL ────────────────────────────────────────
  // fetch() with no-store skips both reading and writing the HTTP cache.
  // The resulting blob URL is always same-origin → canvas never tainted.
  try {
    const resp = await fetch(cacheBustedSrc, {
      credentials: 'omit',
      cache: 'no-store'
    })
    if (resp.ok) {
      const blob = await resp.blob()
      const blobUrl = URL.createObjectURL(blob)
      const img = await loadImgElement(blobUrl, false)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60)
      return img
    }
  } catch (_) {
    /* fall through to strategy 2 */
  }

  // ── Strategy 2: <img crossOrigin="anonymous"> with cache-busted URL ───────
  // Works when the server sends Access-Control-Allow-Origin and fetch() was
  // blocked (e.g. by a strict CSP or proxy).
  try {
    return await loadImgElement(cacheBustedSrc, true)
  } catch (_) {
    /* fall through to strategy 3 */
  }

  // ── Strategy 3: original URL with CORS (no cache-bust) ────────────────────
  // Handles edge cases where the server rejects unknown query params.
  try {
    return await loadImgElement(src, true)
  } catch (_) {
    /* fall through to strategy 4 */
  }

  // ── Strategy 4: no CORS — display only, export blocked ───────────────────
  // Image shows so the user isn't stranded, but we flag it so handleDone
  // can show a helpful message instead of a raw SecurityError.
  const img = await loadImgElement(src, false)
  img.tainted = true
  return img
}

/** Promisified HTMLImageElement loader. */
function loadImgElement(src, cors) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`img load failed: ${src}`))
    if (cors) img.crossOrigin = 'anonymous'
    img.src = src
  })
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v))
}

function fitDimensions(iw, ih, maxW, maxH) {
  const s = Math.min(maxW / iw, maxH / ih, 1)
  return {
    w: Math.max(1, Math.round(iw * s)),
    h: Math.max(1, Math.round(ih * s))
  }
}

function vpToCanvas(vx, vy, zoom, panX, panY) {
  return { x: (vx - panX) / zoom, y: (vy - panY) / zoom }
}

function buildEraserCursor(sw) {
  const sz = Math.max(sw * 4, 8),
    r = sz / 2
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${sz}" height="${sz}" viewBox="0 0 ${sz} ${sz}"><circle cx="${r}" cy="${r}" r="${r -
    1}" fill="rgba(255,255,255,0.3)" stroke="#555" stroke-width="1.5"/></svg>`
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(
    svg
  )}") ${r} ${r}, crosshair`
}

// ─── View reducer ─────────────────────────────────────────────────────────────

const VIEW_INIT = { zoom: 1, panX: 0, panY: 0 }

function viewReducer(s, a) {
  switch (a.type) {
    case 'SET':
      return { ...s, ...a.payload }
    case 'RESET':
      return a.payload || VIEW_INIT
    case 'WHEEL': {
      const { delta, cx, cy } = a
      const factor = 1 + Math.sign(delta) * ZOOM_STEP
      const nextZoom = clamp(s.zoom * factor, MIN_ZOOM, MAX_ZOOM)
      const ratio = nextZoom / s.zoom
      return {
        zoom: nextZoom,
        panX: cx - (cx - s.panX) * ratio,
        panY: cy - (cy - s.panY) * ratio
      }
    }
    default:
      return s
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

const DrawingCanvas = ({ visible, imageSrc, onDone, onCancel, videoId }) => {
  const [tool, setTool] = useState(TOOLS.PEN)
  const [color, setColor] = useState('#F44336')
  const [strokeWidth, setStrokeWidth] = useState(3)
  const [isDrawing, setIsDrawing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [textInput, setTextInput] = useState('')
  const [textPosition, setTextPosition] = useState(null)
  const [history, setHistory] = useState([])
  const [view, dispatchView] = useReducer(viewReducer, VIEW_INIT)

  const canvasRef = useRef(null)
  const bgCanvasRef = useRef(null)
  const containerRef = useRef(null)
  const loadTimerRef = useRef(null)
  const isDrawingRef = useRef(false)
  const circleStartRef = useRef(null)
  const snapshotRef = useRef(null)
  const panStartRef = useRef(null)
  const isPanningRef = useRef(false)
  const viewRef = useRef(view)
  const toolRef = useRef(tool)
  const taintedRef = useRef(false) // true when bg canvas is CORS-tainted and cannot be exported

  useEffect(() => {
    isDrawingRef.current = isDrawing
  }, [isDrawing])
  useEffect(() => {
    viewRef.current = view
  }, [view])
  useEffect(() => {
    toolRef.current = tool
  }, [tool])

  const { uploadFiles } = usePresignedUpload({ videoId })

  // ── Image loading ──────────────────────────────────────────────────────────

  const renderImageToCanvas = useCallback(img => {
    const bg = bgCanvasRef.current
    const draw = canvasRef.current
    const cont = containerRef.current
    if (!bg || !draw || !cont) return

    // Track whether this image will taint the canvas (blocks toBlob export)
    taintedRef.current = !!img.tainted

    const maxW = Math.max(cont.clientWidth || 800, 100)
    const maxH = Math.max(cont.clientHeight || 500, 100)
    const { w, h } = fitDimensions(
      img.naturalWidth || img.width,
      img.naturalHeight || img.height,
      maxW,
      maxH
    )

    bg.width = w
    bg.height = h
    draw.width = w
    draw.height = h
    bg.getContext('2d').drawImage(img, 0, 0, w, h)
    draw.getContext('2d').clearRect(0, 0, w, h)

    const z = Math.min((maxW - 64) / w, (maxH - 64) / h, 1)
    dispatchView({
      type: 'RESET',
      payload: { zoom: z, panX: (maxW - w * z) / 2, panY: (maxH - h * z) / 2 }
    })
    setHistory([])
    setIsLoading(false)
  }, [])

  useEffect(() => {
    if (!visible) {
      clearTimeout(loadTimerRef.current)
      ;[bgCanvasRef, canvasRef].forEach(r => {
        if (r.current)
          r.current
            .getContext('2d')
            .clearRect(0, 0, r.current.width, r.current.height)
      })
      setHistory([])
      setIsLoading(false)
      return
    }
    if (!imageSrc) return
    setIsLoading(true)
    clearTimeout(loadTimerRef.current)
    loadTimerRef.current = setTimeout(async () => {
      try {
        renderImageToCanvas(await loadImage(imageSrc))
      } catch (err) {
        console.error('[DrawingCanvas]', err)
        setIsLoading(false)
        message.error('Could not load image — please try again.')
      }
    }, LOAD_DELAY_MS)
    return () => clearTimeout(loadTimerRef.current)
  }, [visible, imageSrc, renderImageToCanvas])

  // ── Zoom helpers ───────────────────────────────────────────────────────────

  const handleFitToScreen = useCallback(() => {
    const c = canvasRef.current
    const cont = containerRef.current
    if (!c || !cont) return
    const mw = cont.clientWidth,
      mh = cont.clientHeight
    const z = Math.min((mw - 64) / c.width, (mh - 64) / c.height, 1)
    dispatchView({
      type: 'RESET',
      payload: {
        zoom: z,
        panX: (mw - c.width * z) / 2,
        panY: (mh - c.height * z) / 2
      }
    })
  }, [])

  const handleZoom = useCallback(dir => {
    const cont = containerRef.current
    if (!cont) return
    dispatchView({
      type: 'WHEEL',
      delta: dir,
      cx: cont.clientWidth / 2,
      cy: cont.clientHeight / 2
    })
  }, [])

  // Wheel zoom — passive:false so we can preventDefault
  useEffect(() => {
    const el = containerRef.current
    if (!el || !visible) return
    const onWheel = e => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      dispatchView({
        type: 'WHEEL',
        delta: -e.deltaY,
        cx: e.clientX - rect.left,
        cy: e.clientY - rect.top
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [visible])

  // ── History ────────────────────────────────────────────────────────────────

  const saveState = useCallback(() => {
    const c = canvasRef.current
    if (!c) return
    const snap = c.getContext('2d').getImageData(0, 0, c.width, c.height)
    setHistory(prev => {
      const next = [...prev, snap]
      return next.length > MAX_HISTORY
        ? next.slice(next.length - MAX_HISTORY)
        : next
    })
  }, [])

  const handleUndo = useCallback(() => {
    const c = canvasRef.current
    if (!c || history.length === 0) return
    const ctx = c.getContext('2d')
    if (history.length === 1) {
      ctx.clearRect(0, 0, c.width, c.height)
      setHistory([])
      return
    }
    ctx.putImageData(history[history.length - 2], 0, 0)
    setHistory(p => p.slice(0, -1))
  }, [history])

  // ── Drawing ────────────────────────────────────────────────────────────────

  const eventToCanvas = useCallback(e => {
    const cont = containerRef.current
    if (!cont) return { x: 0, y: 0 }
    const rect = cont.getBoundingClientRect()
    const { zoom, panX, panY } = viewRef.current
    return vpToCanvas(
      e.clientX - rect.left,
      e.clientY - rect.top,
      zoom,
      panX,
      panY
    )
  }, [])

  const applyStyle = useCallback(
    ctx => {
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      if (tool === TOOLS.ERASER) {
        ctx.globalCompositeOperation = 'destination-out'
        ctx.lineWidth = Math.max(strokeWidth * 4, 4)
      } else {
        ctx.globalCompositeOperation = 'source-over'
        ctx.strokeStyle = color
        ctx.lineWidth = strokeWidth
      }
    },
    [tool, color, strokeWidth]
  )

  const drawEllipse = useCallback(
    (ctx, s, end) => {
      const cx = (s.x + end.x) / 2,
        cy = (s.y + end.y) / 2
      const rx = Math.max(Math.abs(end.x - s.x) / 2, 0.1)
      const ry = Math.max(Math.abs(end.y - s.y) / 2, 0.1)
      ctx.beginPath()
      ctx.ellipse(cx, cy, rx, ry, 0, 0, 2 * Math.PI)
      ctx.globalCompositeOperation = 'source-over'
      ctx.strokeStyle = color
      ctx.lineWidth = strokeWidth
      ctx.stroke()
    },
    [color, strokeWidth]
  )

  const handleMouseDown = useCallback(
    e => {
      if (e.button !== 0) return
      const c = canvasRef.current
      if (!c) return

      if (toolRef.current === TOOLS.PAN || e.altKey) {
        isPanningRef.current = true
        panStartRef.current = {
          mouseX: e.clientX,
          mouseY: e.clientY,
          panX: viewRef.current.panX,
          panY: viewRef.current.panY
        }
        return
      }

      const pos = eventToCanvas(e)
      setIsDrawing(true)

      if (toolRef.current === TOOLS.TEXT) {
        setTextPosition(pos)
        return
      }

      const ctx = c.getContext('2d')
      if (toolRef.current === TOOLS.CIRCLE) {
        circleStartRef.current = pos
        snapshotRef.current = ctx.getImageData(0, 0, c.width, c.height)
        return
      }

      ctx.beginPath()
      ctx.moveTo(pos.x, pos.y)
      applyStyle(ctx)
    },
    [eventToCanvas, applyStyle]
  )

  const handleMouseMove = useCallback(
    e => {
      if (isPanningRef.current && panStartRef.current) {
        dispatchView({
          type: 'SET',
          payload: {
            panX:
              panStartRef.current.panX +
              (e.clientX - panStartRef.current.mouseX),
            panY:
              panStartRef.current.panY +
              (e.clientY - panStartRef.current.mouseY)
          }
        })
        return
      }
      if (!isDrawingRef.current) return
      const c = canvasRef.current
      if (!c) return
      const pos = eventToCanvas(e)
      const ctx = c.getContext('2d')

      if (toolRef.current === TOOLS.PEN || toolRef.current === TOOLS.ERASER) {
        applyStyle(ctx)
        ctx.lineTo(pos.x, pos.y)
        ctx.stroke()
        return
      }

      if (
        toolRef.current === TOOLS.CIRCLE &&
        circleStartRef.current &&
        snapshotRef.current
      ) {
        ctx.putImageData(snapshotRef.current, 0, 0)
        drawEllipse(ctx, circleStartRef.current, pos)
      }
    },
    [eventToCanvas, applyStyle, drawEllipse]
  )

  const commitDrawing = useCallback(
    e => {
      if (isPanningRef.current) {
        isPanningRef.current = false
        panStartRef.current = null
        return
      }
      if (!isDrawingRef.current) return
      setIsDrawing(false)
      const c = canvasRef.current
      if (!c) return
      const ctx = c.getContext('2d')
      ctx.globalCompositeOperation = 'source-over'

      if (toolRef.current === TOOLS.CIRCLE && circleStartRef.current) {
        if (snapshotRef.current) ctx.putImageData(snapshotRef.current, 0, 0)
        drawEllipse(
          ctx,
          circleStartRef.current,
          e ? eventToCanvas(e) : circleStartRef.current
        )
        circleStartRef.current = null
        snapshotRef.current = null
      }
      saveState()
    },
    [drawEllipse, eventToCanvas, saveState]
  )

  const handleMouseLeave = useCallback(() => {
    if (isPanningRef.current) {
      isPanningRef.current = false
      panStartRef.current = null
    }
    if (isDrawingRef.current) commitDrawing(null)
  }, [commitDrawing])

  // ── Text ───────────────────────────────────────────────────────────────────

  const handleTextSubmit = useCallback(() => {
    const t = textInput.trim()
    if (!t || !textPosition) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    ctx.globalCompositeOperation = 'source-over'
    ctx.font = `600 ${Math.max(
      14,
      strokeWidth * 5
    )}px 'Google Sans', 'Product Sans', sans-serif`
    ctx.fillStyle = color
    ctx.fillText(t, textPosition.x, textPosition.y)
    setTextInput('')
    setTextPosition(null)
    saveState()
  }, [textInput, textPosition, strokeWidth, color, saveState])

  const handleTextCancel = useCallback(() => {
    setTextPosition(null)
    setTextInput('')
  }, [])

  const handleClearAll = useCallback(() => {
    const c = canvasRef.current
    if (!c) return
    c.getContext('2d').clearRect(0, 0, c.width, c.height)
    saveState()
  }, [saveState])

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleDone = useCallback(async () => {
    const bg = bgCanvasRef.current
    const draw = canvasRef.current
    if (!bg || !draw) return

    // Guard: if the image loaded without CORS the canvas is tainted and
    // toBlob will throw a SecurityError — tell the user clearly instead.
    if (taintedRef.current) {
      message.error(
        'Cannot export: the image server does not allow cross-origin access. Contact your admin to enable CORS on the media server.'
      )
      return
    }

    setIsSaving(true)
    try {
      const merged = document.createElement('canvas')
      merged.width = bg.width
      merged.height = bg.height
      const ctx = merged.getContext('2d')
      ctx.drawImage(bg, 0, 0)
      ctx.drawImage(draw, 0, 0)
      const blob = await new Promise((res, rej) =>
        merged.toBlob(
          b => (b ? res(b) : rej(new Error('toBlob returned null'))),
          'image/png'
        )
      )
      const response = await uploadFiles({
        files: [new File([blob], 'annotated-image.png', { type: 'image/png' })],
        category: 'image'
      })
      if (response?.success && response.urls?.[0]) {
        onDone(response.urls[0])
        message.success('Annotated image saved')
      } else {
        throw response?.error || new Error('Upload failed')
      }
    } catch (err) {
      // Re-check for taint — can happen if the browser cached a non-CORS
      // response before the fetch strategy could replace it.
      if (err instanceof DOMException && err.name === 'SecurityError') {
        message.error(
          'Cannot export: image is cross-origin tainted. Please reload and try again.'
        )
        console.error(
          '[DrawingCanvas] SecurityError on toBlob — canvas is tainted',
          err
        )
      } else {
        console.error('[DrawingCanvas] save', err)
        message.error('Failed to save — please try again.')
      }
    } finally {
      setIsSaving(false)
    }
  }, [uploadFiles, onDone])

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!visible) return
    const fn = e => {
      if (e.target.tagName === 'INPUT') return
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault()
        handleUndo()
      }
      if (e.key === 'Escape') handleTextCancel()
      if (e.key === '=' || e.key === '+') handleZoom(1)
      if (e.key === '-') handleZoom(-1)
      if (e.key === '0') handleFitToScreen()
      if (e.key === 'p') setTool(TOOLS.PEN)
      if (e.key === 'e') setTool(TOOLS.CIRCLE)
      if (e.key === 't') setTool(TOOLS.TEXT)
      if (e.key === 'x') setTool(TOOLS.ERASER)
      if (e.key === 'h') setTool(TOOLS.PAN)
    }
    window.addEventListener('keydown', fn)
    return () => window.removeEventListener('keydown', fn)
  }, [visible, handleUndo, handleTextCancel, handleZoom, handleFitToScreen])

  // ── Derived values ─────────────────────────────────────────────────────────

  const cursorStyle = useMemo(() => {
    if (tool === TOOLS.PAN) return 'grab'
    if (tool === TOOLS.TEXT) return 'text'
    if (tool === TOOLS.ERASER) return buildEraserCursor(strokeWidth)
    return 'crosshair'
  }, [tool, strokeWidth])

  const toolDefs = useMemo(
    () => [
      { key: TOOLS.PEN, label: 'Pen', Icon: Icons.Pen, tip: 'Pen  P' },
      {
        key: TOOLS.CIRCLE,
        label: 'Circle',
        Icon: Icons.Circle,
        tip: 'Circle  E'
      },
      { key: TOOLS.TEXT, label: 'Text', Icon: Icons.Text, tip: 'Text  T' },
      {
        key: TOOLS.ERASER,
        label: 'Eraser',
        Icon: Icons.Eraser,
        tip: 'Eraser  X'
      },
      {
        key: TOOLS.PAN,
        label: 'Pan',
        Icon: Icons.Pan,
        tip: 'Pan  H  or Alt+drag'
      }
    ],
    []
  )

  const zoomPct = Math.round(view.zoom * 100)
  const canvasXfrm = `translate(${view.panX}px,${view.panY}px) scale(${view.zoom})`

  // Compute text popup viewport position
  const textPopupStyle = useMemo(() => {
    if (!textPosition) return {}
    return {
      left: textPosition.x * view.zoom + view.panX + 8,
      top: textPosition.y * view.zoom + view.panY + 8
    }
  }, [textPosition, view])

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={visible}
      title={null}
      footer={null}
      width="min(980px, 96vw)"
      centered
      maskClosable={false}
      onCancel={() => {
        blurActiveElement()
        onCancel()
      }}
      afterClose={blurActiveElement}
      className="dc-modal"
      bodyStyle={{ padding: 0 }}
      destroyOnClose
    >
      <div className="dc-root">
        {/* Header */}
        <div className="dc-header">
          <div className="dc-header__brand">
            <div className="dc-header__icon-wrap">
              <Icons.Pen />
            </div>
            <div>
              <div className="dc-header__title">Annotate Image</div>
              <div className="dc-header__subtitle">
                Draw, mark up, then send to AI
              </div>
            </div>
          </div>
          <div className="dc-header__actions">
            <button
              className="dc-btn dc-btn--ghost"
              onClick={onCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className={`dc-btn dc-btn--primary${
                isSaving ? ' dc-btn--loading' : ''
              }`}
              onClick={handleDone}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="dc-spinner" />
                  Saving…
                </>
              ) : (
                <>
                  <span className="dc-btn__icon">
                    <Icons.Check />
                  </span>
                  Use Annotated Image
                </>
              )}
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="dc-toolbar">
          {/* Drawing tools */}
          <div className="dc-toolbar__group">
            {toolDefs.map(({ key, label, Icon, tip }) => (
              <Tooltip
                title={tip}
                placement="bottom"
                mouseEnterDelay={0.6}
                key={key}
              >
                <button
                  className={`dc-tool-btn${
                    tool === key ? ' dc-tool-btn--active' : ''
                  }`}
                  onClick={() => setTool(key)}
                  aria-label={label}
                  aria-pressed={tool === key}
                >
                  <span className="dc-tool-btn__icon">
                    <Icon />
                  </span>
                  <span className="dc-tool-btn__label">{label}</span>
                </button>
              </Tooltip>
            ))}
          </div>

          <div className="dc-toolbar__sep" />

          {/* Colors */}
          <div className="dc-toolbar__group dc-toolbar__group--colors">
            {DEFAULT_COLORS.map(c => (
              <button
                key={c}
                className={`dc-swatch${
                  color === c ? ' dc-swatch--active' : ''
                }`}
                style={{ '--c': c }}
                onClick={() => setColor(c)}
                aria-label={c}
              />
            ))}
          </div>

          <div className="dc-toolbar__sep" />

          {/* Stroke size */}
          <div className="dc-toolbar__group dc-toolbar__group--stroke">
            <span className="dc-stroke-label">
              <svg width="32" height="12" viewBox="0 0 32 12">
                <rect
                  x="0"
                  y={6 - strokeWidth / 6}
                  width="32"
                  height={Math.max(1, strokeWidth / 3)}
                  rx="1"
                  fill="currentColor"
                />
              </svg>
              <span>{strokeWidth}px</span>
            </span>
            <div className="dc-slider-wrap">
              <input
                type="range"
                min={1}
                max={20}
                value={strokeWidth}
                onChange={e => setStrokeWidth(Number(e.target.value))}
                className="dc-slider"
              />
            </div>
          </div>

          <div className="dc-toolbar__sep" />

          {/* Undo / Clear */}
          <div className="dc-toolbar__group">
            <Tooltip
              title="Undo  Ctrl+Z"
              placement="bottom"
              mouseEnterDelay={0.6}
            >
              <button
                className="dc-icon-btn"
                onClick={handleUndo}
                disabled={history.length === 0}
                aria-label="Undo"
              >
                <Icons.Undo />
              </button>
            </Tooltip>
            <Tooltip title="Clear all" placement="bottom" mouseEnterDelay={0.6}>
              <button
                className="dc-icon-btn"
                onClick={handleClearAll}
                aria-label="Clear"
              >
                <Icons.Clear />
              </button>
            </Tooltip>
          </div>

          <div className="dc-toolbar__sep" />

          {/* Zoom */}
          <div className="dc-toolbar__group dc-toolbar__group--zoom">
            <Tooltip
              title="Zoom out  −"
              placement="bottom"
              mouseEnterDelay={0.6}
            >
              <button
                className="dc-icon-btn"
                onClick={() => handleZoom(-1)}
                aria-label="Zoom out"
              >
                <Icons.ZoomOut />
              </button>
            </Tooltip>
            <span className="dc-zoom-chip">{zoomPct}%</span>
            <Tooltip
              title="Zoom in  +"
              placement="bottom"
              mouseEnterDelay={0.6}
            >
              <button
                className="dc-icon-btn"
                onClick={() => handleZoom(1)}
                aria-label="Zoom in"
              >
                <Icons.ZoomIn />
              </button>
            </Tooltip>
            <Tooltip
              title="Fit to screen  0"
              placement="bottom"
              mouseEnterDelay={0.6}
            >
              <button
                className="dc-icon-btn"
                onClick={handleFitToScreen}
                aria-label="Fit to screen"
              >
                <Icons.Fit />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Canvas area */}
        <div
          className="dc-canvas-area"
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={commitDrawing}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: isPanningRef.current ? 'grabbing' : cursorStyle }}
        >
          {isLoading && (
            <div className="dc-loader">
              <Spin size="large" />
              <span>Loading image…</span>
            </div>
          )}

          <div
            className="dc-canvas-wrap"
            style={{
              transform: canvasXfrm,
              transformOrigin: '0 0',
              opacity: isLoading ? 0 : 1
            }}
          >
            <canvas ref={bgCanvasRef} className="dc-bg-canvas" />
            <canvas ref={canvasRef} className="dc-draw-canvas" />
          </div>

          {/* Text input popup — position in viewport space over the canvas */}
          {textPosition && (
            <div className="dc-text-popup" style={textPopupStyle}>
              <input
                autoFocus
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleTextSubmit()
                  if (e.key === 'Escape') handleTextCancel()
                }}
                placeholder="Type and press Enter…"
                className="dc-text-popup__input"
              />
              <button
                className="dc-btn dc-btn--primary dc-btn--sm"
                onClick={handleTextSubmit}
              >
                Add
              </button>
              <button
                className="dc-btn dc-btn--ghost dc-btn--sm"
                onClick={handleTextCancel}
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {/* Status bar */}
        <div className="dc-statusbar">
          <span>Scroll to zoom · Alt+drag or H to pan · Ctrl+Z to undo</span>
          <span className="dc-statusbar__zoom">{zoomPct}%</span>
        </div>
      </div>
    </Modal>
  )
}

export default DrawingCanvas
